import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { OverlayTrigger, Tooltip, Form } from "react-bootstrap";
import { useFormik } from "formik";
import { fetchWithTokenRefresh, getCSRFToken } from "views_shared/app/javascript/api/withTokenRefresh";
import MarketingPersonaPicker from "../../shared/MarketingPersonaPicker";
import useSelectedExperts from "../../expert_picker/useSelectedExperts";
import { MIN_EXPERTS, MAX_EXPERTS } from "../../expert_picker/constants";
import EmailPreview from "../EmailPreview";
import TopicsInput from "./TopicsInput";
import ExpertsField from "./ExpertsField";
import RecipientsField from "./RecipientsField";
import SendGuardModal from "./SendGuardModal";
import { fetchUserProfiles, fetchUserNotificationEligibility } from "../../recipient_picker/api";
import { validationSchema } from "../storyIdeaValidation";

const BLANK_INITIAL_VALUES = {
  title: "",
  news_hook: "",
  topics: [],
  marketing_persona_id: "",
  // Admin curates recipients upfront via the picker (#3143); the picker
  // captures per-search ConfigFilter settings inside the groups'
  // source_params, so custom_recipient_match_config is no longer set by
  // this form. Kept on the StoryIdea column for backwards-compat with
  // legacy records.
  custom_recipient_match_config: null,
  selected_expert_ids: [],
  selected_recipient_ids: [],
  selected_recipient_groups: [],
  // AI vet verdicts. Hash keyed by user UUID. Produced by the "Vet
  // visible" action inside the recipient picker (the picker owns
  // useVerdictBatching now) and flushed back here via onVerdictsChange.
  // Each entry carries a `vetted_against` digest so the picker can detect
  // per-verdict staleness when the prompt-inputs change. Persisted as
  // MatchFeed::StoryIdea#ai_verdicts on save.
  ai_verdicts: {},
};

// Seed initialValues from a loaded StoryIdea (#3121 edit mode). When
// initialStoryIdea is null we're creating a new row and use blank
// defaults. When it's populated (page fetched it before mounting us)
// we merge its fields over the blanks so formik picks them up on
// mount. Keep this function pure — it's also called from tests.
export function buildInitialValues(initialStoryIdea) {
  if (!initialStoryIdea) return BLANK_INITIAL_VALUES;
  return {
    ...BLANK_INITIAL_VALUES,
    title: initialStoryIdea.title ?? "",
    news_hook: initialStoryIdea.news_hook ?? "",
    topics: initialStoryIdea.topics ?? [],
    marketing_persona_id: initialStoryIdea.from_id ?? "",
    custom_recipient_match_config: initialStoryIdea.custom_recipient_match_config ?? null,
    selected_expert_ids: initialStoryIdea.selected_expert_ids ?? [],
    selected_recipient_ids: initialStoryIdea.selected_recipient_ids ?? [],
    selected_recipient_groups: initialStoryIdea.selected_recipient_groups ?? [],
    ai_verdicts: initialStoryIdea.ai_verdicts ?? {},
  };
}

const StoryIdeaForm = React.forwardRef(function StoryIdeaForm(
  { initialStoryIdea, onSuccess, onClose, onSubmittingChange, onRecipientCountChange, onIsValidChange },
  ref,
) {
  // `editing` is computed once on mount. We don't expect initialStoryIdea
  // to switch identity after the form renders (the page mounts us with a
  // fixed value), so re-deriving on every render is fine.
  const editing = Boolean(initialStoryIdea?.id);
  const editId = initialStoryIdea?.id;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [topicsValidationState, setTopicsValidationState] = useState({ isValid: false, isInvalid: false });
  const [marketingPersonaValidationState, setMarketingPersonaValidationState] = useState({
    isValid: false,
    isInvalid: false,
  });
  const [mainSelectedMarketingPersona, setMainSelectedMarketingPersona] = useState(null); // Store selected marketing persona for single form

  // EmailPreview previously read its content from "committed" (blurred)
  // title/news_hook so that keystrokes didn't fire similarity-search calls
  // in the right column. With #3143 the right column is no longer a live
  // preview surface; EmailPreview reads directly from formik. The setter
  // is still called (on blur + on reset) so the structure is in place if
  // a debounced read ever returns; the value itself is currently unread.
  const [, setCommittedValues] = useState({ title: "", news_hook: "" });

  // Display-only mirror of the picker's saved pool with full candidate
  // details (name, workplace, etc.). Populated when RecipientPicker's
  // Save flushes via onPickerSave. The form's canonical state is
  // formik's selected_recipient_ids + selected_recipient_groups —
  // this is just so the inline RecipientSummary can render details
  // without re-fetching from PostgREST after each Save.
  const [recipientDisplayPool, setRecipientDisplayPool] = useState([]);

  // Edit-mode hydration of recipientDisplayPool: if we mounted with
  // initialStoryIdea carrying selected_recipient_ids, fetch the user
  // details so the inline RecipientSummary shows the recipients
  // immediately (matching the picker's own hydration behaviour for
  // deleted users — synthesize a "(deleted user)" stub for any UUID
  // that admin_recipients_lookup doesn't return). Without this the
  // header reads "Recipients (17)" but the body is empty until the
  // admin opens and re-saves the picker.
  useEffect(() => {
    const ids = initialStoryIdea?.selected_recipient_ids;
    if (!ids || ids.length === 0) return undefined;

    // Per-recipient source provenance from the persisted groups.
    // selected_recipient_groups carries one { source_type,
    // source_params, user_ids } entry per search that contributed
    // recipients, so RecipientSummary can render the "Group by search"
    // toggle and grouped sections when a draft is reopened. Mirror
    // useCandidatePool's groups → sourceById index so the per-search
    // provenance survives the round-trip.
    const sourceById = new Map();
    (initialStoryIdea?.selected_recipient_groups || []).forEach((group) => {
      const source = {
        type: group.source_type || "previously_saved",
        params: group.source_params || {},
      };
      (group.user_ids || []).forEach((uid) => {
        sourceById.set(String(uid).toLowerCase(), source);
      });
    });

    let cancelled = false;
    // Two parallel lookups: profile-details from admin_profiles_lookup
    // (name, workplace, bio, avatar, etc.) and notification-eligibility
    // from the parameterised RPC. They're split because coupling
    // profile-display with notification-eligibility produces silently-
    // broken results (an opted-out user disappears from a profile
    // picker that should still show them). The notification db_name
    // here matches the notification class the picker enforces — see
    // Notify::Notification::Media::NewStoryIdea#db_name.
    Promise.all([fetchUserProfiles(ids), fetchUserNotificationEligibility(ids, "media_new_story_idea")])
      .then(([profiles, eligibilities]) => {
        if (cancelled) return;
        // Defensive lookup: PostgreSQL UUIDs are case-insensitive
        // logically but JavaScript string comparison isn't. Normalise
        // both sides to lowercase before matching so the "(deleted
        // user)" stub fallback only fires when the profile genuinely
        // didn't come back.
        const profilesById = new Map(profiles.map((r) => [String(r.id).toLowerCase(), r]));
        const eligibilityById = new Map(eligibilities.map((r) => [String(r.user_id).toLowerCase(), r]));
        const hydrated = ids.map((id) => {
          const idStr = String(id);
          const profile = profilesById.get(idStr.toLowerCase());
          const elig = eligibilityById.get(idStr.toLowerCase());
          return {
            id: idStr,
            name: profile?.name || "(deleted user)",
            email: profile?.email || null,
            workplace: profile?.workplace_name || null,
            title: profile?.title_at_workplace || null,
            headline: profile?.headline || null,
            bio: profile?.bio || null,
            last_active_at: profile?.last_active_at || null,
            profile_image_url: profile?.profile_image_url || null,
            is_eligible: elig?.is_eligible ?? null,
            ineligibility_reasons: elig?.reasons || [],
            is_stub: !profile,
            // Fall back to "previously_saved" only when there's no
            // matching group entry (stray legacy id without a group,
            // or a recipient added directly via the API). Keeps the
            // shape coherent without losing per-search info when it's
            // available.
            source: sourceById.get(idStr.toLowerCase()) || { type: "previously_saved", params: {} },
          };
        });
        setRecipientDisplayPool(hydrated);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("[StoryIdeaForm] recipient pool hydration failed", err);
      });

    return () => {
      cancelled = true;
    };
    // Hydrate once on mount with the initialStoryIdea snapshot — same
    // policy as the experts hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The submit buttons each carry an intent ("close" = Create, "keep-open" =
  // Save and create another). Set just before triggering formik.submitForm()
  // and read once in onSubmit's success path, which dispatches post-submit
  // behaviour by intent. A new submit variant (#3121 schedule, #3146
  // test-send) is a new case in that switch, not another ref + branch.
  //
  // Reset ownership: "keep-open" stays mounted, so the form owns the
  // soft-reset of its own state. "close" hands off to the host (page or
  // modal); the host disposes us, so we don't reset state we're about to
  // unmount with.
  const submitIntentRef = useRef("close");

  // formikRef keeps a stable handle so effects/callbacks don't re-run when
  // formik recreates its identity each render. Declared before useFormik so
  // the experts hook (whose onUserChange touches the field) can be defined
  // ahead of the formik onSubmit that references `experts`.
  const formikRef = useRef(null);

  // Experts selection (state, edit-mode hydration, add/remove/suggest/clear
  // merge policy) lives in the hook. The form keeps only the formik glue:
  // mirror the selection into the `selected_expert_ids` field, and mark it
  // touched on user-initiated changes (not on hydration).
  const markExpertsTouched = useCallback(() => {
    formikRef.current?.setFieldTouched("selected_expert_ids", true);
  }, []);
  // initialIds for the experts hook is computed once on mount — same
  // lifetime as initialStoryIdea.
  const seededInitialValues = useMemo(() => buildInitialValues(initialStoryIdea), []); // eslint-disable-line react-hooks/exhaustive-deps
  const experts = useSelectedExperts({
    initialIds: seededInitialValues.selected_expert_ids,
    maxSelection: MAX_EXPERTS,
    onUserChange: markExpertsTouched,
  });

  // Shared HTTP submission helper (#3121). Called from both formik's
  // onSubmit (the Schedule / Save-and-create-another paths) and from
  // the direct saveAsDraft handle on the imperative ref (which
  // deliberately skips formik.submitForm to avoid marking every field
  // touched — that's what was making partial-draft saves show inline
  // experts/recipients errors).
  const performHttpSubmit = useCallback(
    async (values, intent) => {
      setIsSubmitting(true);
      try {
        // fetchWithTokenRefresh transparently handles 401 → refresh
        // access_token → retry so a save click after a long form
        // session doesn't dump the admin's work.
        //
        // PATCH on edit, POST on create — both endpoints take the same
        // { story_idea: ... } body and return the serialized row.
        const url = editing ? `/admin/story_ideas/${editId}` : "/admin/story_ideas";
        const method = editing ? "PATCH" : "POST";
        const response = await fetchWithTokenRefresh(url, {
          method,
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": getCSRFToken(),
          },
          body: JSON.stringify({
            story_idea: {
              title: values.title,
              news_hook: values.news_hook,
              topics: values.topics,
              from_id: values.marketing_persona_id,
              custom_recipient_match_config: values.custom_recipient_match_config,
              selected_expert_ids: values.selected_expert_ids,
              selected_recipient_ids: values.selected_recipient_ids,
              selected_recipient_groups: values.selected_recipient_groups,
              ai_verdicts: values.ai_verdicts,
            },
          }),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.errors?.join(", ") || `HTTP ${response.status}: ${response.statusText}`);
        }
        const savedStoryIdea = await response.json();
        if (onSuccess) onSuccess(savedStoryIdea, intent);
        return savedStoryIdea;
      } catch (error) {
        formikRef.current?.setErrors({ general: error.message });
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [editing, editId, onSuccess],
  );

  // Formik form management
  const formik = useFormik({
    initialValues: seededInitialValues,
    validationSchema,
    validate: (values) => {
      const errors = {};
      const validations = [
        { field: "title", required: true, maxLength: 255, message: "title" },
        { field: "news_hook", required: true, maxLength: 10000, message: "news hook" },
      ];

      validations.forEach(({ field, required, maxLength, message }) => {
        const value = values[field];
        if (required && !value) {
          errors[field] = `Please enter a ${message}`;
        } else if (value && maxLength && value.length > maxLength) {
          errors[field] =
            `${message.charAt(0).toUpperCase() + message.slice(1)} must be ${maxLength.toLocaleString()} characters or less`;
        }
      });

      const expertCount = (values.selected_expert_ids || []).length;
      if (expertCount < MIN_EXPERTS || expertCount > MAX_EXPERTS) {
        errors.selected_expert_ids = `Select between ${MIN_EXPERTS} and ${MAX_EXPERTS} experts`;
      }

      const recipientCount = (values.selected_recipient_ids || []).length;
      if (recipientCount < 1) {
        errors.selected_recipient_ids = "Pick at least one recipient";
      }

      return errors;
    },
    onSubmit: async (values, { setSubmitting }) => {
      if (isSubmitting) return;

      // The "save-draft" intent doesn't go through formik.submitForm
      // (which would mark every field touched and surface the
      // experts/recipients/marketing-persona inline errors even though
      // the partial-draft save is fine). For everything else, run the
      // strict pre-checks first.
      if (!values.topics || values.topics.length === 0) {
        setTopicsValidationState({ isValid: false, isInvalid: true });
        formik.setFieldTouched("topics", true);
        return;
      }
      if (
        !values.marketing_persona_id ||
        values.marketing_persona_id === "" ||
        values.marketing_persona_id === "clear"
      ) {
        setMarketingPersonaValidationState({ isValid: false, isInvalid: true });
        formik.setFieldTouched("marketing_persona_id", true);
        return;
      }

      setSubmitting(true);
      const savedStoryIdea = await performHttpSubmit(values, submitIntentRef.current);
      setSubmitting(false);

      if (savedStoryIdea) {
        switch (submitIntentRef.current) {
          case "keep-open":
            // Soft reset: preserve the marketing persona so the admin
            // can rapidly enter the next story idea without re-picking
            // the sender. Form stays mounted. ai_verdicts MUST be
            // cleared explicitly here — without it, formik retains the
            // just-saved row's verdicts and the next PATCH inherits
            // ghost verdicts keyed to a different StoryIdea's recipients.
            formik.resetForm({
              values: {
                title: "",
                news_hook: "",
                topics: [],
                marketing_persona_id: values.marketing_persona_id,
                custom_recipient_match_config: null,
                selected_expert_ids: [],
                selected_recipient_ids: [],
                selected_recipient_groups: [],
                ai_verdicts: {},
              },
            });
            setTopicsValidationState({ isValid: false, isInvalid: false });
            experts.reset();
            setRecipientDisplayPool([]);
            setCommittedValues({ title: "", news_hook: "" });
            break;
          case "schedule":
            // No reset, no close — the page opens the schedule modal in
            // its onSuccess handler keyed off the "schedule" intent.
            break;
          case "close":
          default:
            // Host decides what "close" means (page navigates, modal
            // unmounts, etc.). We don't reset state because we're about
            // to be unmounted — resetting would just dispatch updates
            // into a soon-dead component.
            if (onClose) onClose();
            break;
        }
      }

      // Always reset the intent so a failed submit can't leave a stale
      // intent that hijacks the next click on a different button.
      submitIntentRef.current = "close";
    },
  });

  // Blur handlers that update committed values for similarity search
  const handleTitleBlur = useCallback(
    (e) => {
      formik.handleBlur(e);
      setCommittedValues((prev) => ({ ...prev, title: e.target.value }));
    },
    [formik],
  );

  const handleNewsHookBlur = useCallback(
    (e) => {
      formik.handleBlur(e);
      setCommittedValues((prev) => ({ ...prev, news_hook: e.target.value }));
    },
    [formik],
  );

  // RecipientPicker save handler. Receives { ids, groups, candidates }
  // from useCandidatePool#toFormData. ids + groups are the formik
  // canonical state (POST body); candidates is the in-picker pool with
  // display details, mirrored to local state so the inline summary
  // doesn't have to re-fetch from PostgREST.
  const handlePickerSave = useCallback(
    ({ ids, groups, candidates }) => {
      formik.setFieldValue("selected_recipient_ids", ids);
      formik.setFieldValue("selected_recipient_groups", groups);
      formik.setFieldTouched("selected_recipient_ids", true);
      setRecipientDisplayPool(candidates);
    },
    [formik],
  );

  // Helper function for updating topics field with Formik
  const handleTopicsChange = useCallback(
    (newTopics) => {
      // Update formik state
      formik.setFieldValue("topics", newTopics);
      formik.setFieldTouched("topics", true);

      // Update local validation state immediately
      const hasTopics = newTopics.length > 0;
      setTopicsValidationState({
        isValid: hasTopics,
        isInvalid: !hasTopics,
      });
    },
    [formik],
  );

  // Keep formikRef pointing at the live formik instance, then mirror the
  // hook's selection into the `selected_expert_ids` field. The assignment
  // effect is declared first so formikRef.current is set before the sync
  // effect reads it on mount.
  useEffect(() => {
    formikRef.current = formik;
  });
  useEffect(() => {
    formikRef.current.setFieldValue(
      "selected_expert_ids",
      experts.selected.map((e) => e.id),
    );
  }, [experts.selected]);

  // Report selected recipient count up so the page wrapper can render
  // it in the primary submit button ("Send to N media").
  const recipientCount = formik.values.selected_recipient_ids?.length ?? 0;
  useEffect(() => {
    onRecipientCountChange?.(recipientCount);
  }, [recipientCount, onRecipientCountChange]);

  // AI vet wiring. The vet action lives in the recipient picker now (it
  // owns useVerdictBatching). The form stays the verdict *consumer*: it
  // holds ai_verdicts in formik (for the send-guard + persistence) and
  // passes the prompt-inputs + write-back callbacks down to the picker.
  const verdictStoryIdea = useMemo(
    () => ({
      title: formik.values.title,
      news_hook: formik.values.news_hook,
      topics: formik.values.topics,
    }),
    [formik.values.title, formik.values.news_hook, formik.values.topics],
  );
  const handleVerdictsChange = useCallback((next) => {
    formikRef.current?.setFieldValue("ai_verdicts", next);
  }, []);

  // Push isValid up to the page so it can gate the Schedule button.
  // Ref-pinned callback to avoid re-firing when the parent re-renders
  // (same pattern as onSubmittingChangeRef below).
  //
  // `effectiveValid` is computed directly from formik.values rather than
  // read from formik.isValid because the Yup validationSchema runs
  // asynchronously — `formik.isValid` can lag behind the actual form
  // state (an in-flight Yup promise resolves *after* the user has
  // finished filling the form). A direct synchronous derivation is
  // deterministic and matches what the user sees on screen.
  const effectiveValid = useMemo(() => {
    const v = formik.values;
    const expertCount = v.selected_expert_ids?.length ?? 0;
    return (
      Boolean(v.title?.trim()) &&
      Boolean(v.news_hook?.trim()) &&
      (v.topics?.length ?? 0) >= 1 &&
      Boolean(v.marketing_persona_id) &&
      v.marketing_persona_id !== "clear" &&
      expertCount >= MIN_EXPERTS &&
      expertCount <= MAX_EXPERTS &&
      (v.selected_recipient_ids?.length ?? 0) >= 1
    );
  }, [formik.values]);

  const onIsValidChangeRef = useRef(onIsValidChange);
  useEffect(() => {
    onIsValidChangeRef.current = onIsValidChange;
  });
  useEffect(() => {
    onIsValidChangeRef.current?.(effectiveValid);
  }, [effectiveValid]);

  // Helper function for updating marketing persona field with Formik
  const handleMarketingPersonaChange = useCallback(
    (marketingPersonaId, marketingPersona) => {
      // Update formik state
      formik.setFieldValue("marketing_persona_id", marketingPersonaId);
      formik.setFieldTouched("marketing_persona_id", true);

      // Store the marketing persona object for email preview
      setMainSelectedMarketingPersona(marketingPersona);

      // Update local validation state immediately
      const hasStaffMember = Boolean(marketingPersonaId) && marketingPersonaId !== "" && marketingPersonaId !== "clear";
      setMarketingPersonaValidationState({
        isValid: hasStaffMember,
        isInvalid: !hasStaffMember,
      });
    },
    [formik],
  );

  // Send-time guard. Computes how many recipients are
  // unvetted vs flagged-bad given the current formik state. The "close"
  // intent (= actual send) shows a confirmation modal when either count
  // is > 0. All-clear sends (zero unvetted, zero bad) skip the modal
  // entirely — that's the key to avoiding muscle-memory dismissal.
  const [showSendGuard, setShowSendGuard] = useState(false);
  const sendGuardCounts = useMemo(() => {
    const verdicts = formik.values.ai_verdicts || {};
    const recipientIds = formik.values.selected_recipient_ids || [];
    let bad = 0;
    let unvetted = 0;
    recipientIds.forEach((id) => {
      const v = verdicts[id]?.verdict;
      if (v === "bad") bad += 1;
      else if (!v) unvetted += 1;
    });
    return { bad, unvetted, total: recipientIds.length };
  }, [formik.values.ai_verdicts, formik.values.selected_recipient_ids]);
  const needsSendGuard = sendGuardCounts.bad > 0 || sendGuardCounts.unvetted > 0;

  // Triggers the formik submission for a given intent. The intent is read
  // back in onSubmit's success path.
  const performSubmit = useCallback(
    (intent) => {
      // Ensure topics validation state is set if never touched
      if (!formik.touched.topics && formik.values.topics.length === 0) {
        setTopicsValidationState({ isValid: false, isInvalid: true });
      }

      // Ensure marketing persona validation state is set if never touched
      if (
        !formik.touched.marketing_persona_id &&
        (!formik.values.marketing_persona_id ||
          formik.values.marketing_persona_id === "" ||
          formik.values.marketing_persona_id === "clear")
      ) {
        setMarketingPersonaValidationState({ isValid: false, isInvalid: true });
      }

      submitIntentRef.current = intent;
      formik.submitForm();
    },
    [formik],
  );

  // Trigger validation + submission for a given intent. On the "close"
  // intent (= actual send) the send-guard modal fires first when bad /
  // unvetted recipients are present. "keep-open" is the save-draft
  // path (currently used only by tests); skip the guard.
  const submitWithIntent = useCallback(
    (intent, e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (intent === "close" && needsSendGuard) {
        setShowSendGuard(true);
        return;
      }

      performSubmit(intent);
    },
    [needsSendGuard, performSubmit],
  );

  const handleSendAnyway = useCallback(() => {
    setShowSendGuard(false);
    performSubmit("close");
  }, [performSubmit]);

  // Surface submitting state to the parent via callback (replaces the old
  // setInterval poll in StoryIdeasIndex). Ref so a new parent identity each
  // render doesn't re-fire the effect.
  const onSubmittingChangeRef = useRef(onSubmittingChange);
  useEffect(() => {
    onSubmittingChangeRef.current = onSubmittingChange;
  });
  useEffect(() => {
    onSubmittingChangeRef.current?.(isSubmitting);
  }, [isSubmitting]);

  // Expose methods to parent component.
  //
  // #3121 added two intents that bypass submitWithIntent's send-guard
  // detour: saveAsDraft (looser validation, just persist) and
  // saveAndScheduleIntent (full validation, then the page opens its
  // schedule modal in the onSuccess handler keyed off the "schedule"
  // intent). The existing submit / submitAndCreateAnother handles stay
  // for backwards-compat with tests + the older flow.
  React.useImperativeHandle(ref, () => ({
    submit: (e) => submitWithIntent("close", e),
    submitAndCreateAnother: (e) => submitWithIntent("keep-open", e),
    saveAsDraft: (e) => {
      // Save Draft deliberately bypasses formik.submitForm because that
      // method marks every field touched, which would surface the
      // experts/recipients/marketing-persona inline errors even though
      // partial drafts are valid (model uses conditional validators).
      // We call performHttpSubmit directly so only the title-only
      // hard requirement is enforced client-side, matching the
      // backend's draft-permissive validators.
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (isSubmitting) return;
      if (!formikRef.current?.values?.title?.trim()) {
        formikRef.current?.setFieldError("title", "Please enter a title");
        formikRef.current?.setFieldTouched("title", true);
        return;
      }
      performHttpSubmit(formikRef.current.values, "save-draft");
    },
    saveAndScheduleIntent: (e) => {
      // Full-shape validation + save. The page reads the "schedule"
      // intent in its onSuccess to open the schedule modal with the
      // saved row's id. Skipping the send-guard because the schedule
      // modal is the next confirmation step.
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      performSubmit("schedule");
    },
    reset: () => {
      formik.resetForm();
      setIsSubmitting(false);
      setTopicsValidationState({ isValid: false, isInvalid: false });
      setMarketingPersonaValidationState({ isValid: false, isInvalid: false });
      setMainSelectedMarketingPersona(null);
      experts.reset();
      setRecipientDisplayPool([]);
      setCommittedValues({ title: "", news_hook: "" });
    },
  }));

  return (
    <div>
      <CollapsibleSection title="Email" defaultOpen>
        <div className="d-flex">
          {/* Form Section (left) */}
          <div style={{ flex: "1 1 40%", minWidth: 0, padding: "2rem 2.5rem 2rem 3rem" }}>
            {/* General Error Display */}
            {formik.errors.general && (
              <div className="alert alert-danger mb-3" role="alert">
                {[formik.errors.general].flat().map((error) => (
                  <div key={`formik-${error.slice(0, 20)}`}>{error}</div>
                ))}
              </div>
            )}
            <div className="mb-3">
              <label className="form-label" htmlFor="marketing-persona-picker">
                From <span className="text-danger">*</span>
                <OverlayTrigger
                  key="right"
                  placement="right"
                  overlay={
                    <Tooltip id="tooltip-marketing-persona">
                      Select the marketing persona who will be sending this story idea to journalists.
                    </Tooltip>
                  }
                >
                  <i className="fa-solid fa-circle-info text-muted ms-1" style={{ fontSize: "0.8em" }} title="info" />
                </OverlayTrigger>
              </label>
              <MarketingPersonaPicker
                id="marketing-persona-picker"
                value={formik.values.marketing_persona_id}
                onChange={handleMarketingPersonaChange}
                isInvalid={marketingPersonaValidationState.isInvalid}
                isValid={marketingPersonaValidationState.isValid}
              />
              {marketingPersonaValidationState.isInvalid && (
                <div className="invalid-feedback d-block">Please select a persona.</div>
              )}
            </div>
            <div className="mb-3">
              <label htmlFor="title" className="form-label">
                Title <span className="text-danger">*</span>
                <OverlayTrigger
                  key="right"
                  placement="right"
                  overlay={
                    <Tooltip id="tooltip-title">
                      The subject line of the email that will be sent to journalists. It is normally a short summary of
                      the news hook.
                    </Tooltip>
                  }
                >
                  <i className="fa-solid fa-circle-info text-muted ms-1" style={{ fontSize: "0.8em" }} title="info" />
                </OverlayTrigger>
              </label>
              <Form.Control
                type="text"
                id="title"
                name="title"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={handleTitleBlur}
                isInvalid={formik.touched.title && formik.errors.title}
                isValid={formik.touched.title && !formik.errors.title && formik.values.title}
                placeholder="Enter the story title..."
                className="bg-white"
                autoComplete="off"
              />
              <Form.Control.Feedback type="invalid">{formik.errors.title}</Form.Control.Feedback>
            </div>
            <div className="mb-3">
              <label htmlFor="news_hook" className="form-label">
                News Hook <span className="text-danger">*</span>
                <OverlayTrigger
                  key="right"
                  placement="right"
                  overlay={
                    <Tooltip id="tooltip-news-hook">
                      The current news story or event that the journalist coule be writing stories about.
                    </Tooltip>
                  }
                >
                  <i className="fa-solid fa-circle-info text-muted ms-1" style={{ fontSize: "0.8em" }} title="info" />
                </OverlayTrigger>
              </label>
              <Form.Control
                as="textarea"
                id="news_hook"
                name="news_hook"
                rows={6}
                value={formik.values.news_hook}
                onChange={formik.handleChange}
                onBlur={handleNewsHookBlur}
                isInvalid={formik.touched.news_hook && formik.errors.news_hook}
                isValid={formik.touched.news_hook && !formik.errors.news_hook && formik.values.news_hook}
                placeholder="Enter the news hook that will grab attention..."
                className="bg-white"
                autoComplete="off"
              />
              <Form.Control.Feedback type="invalid">{formik.errors.news_hook}</Form.Control.Feedback>
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="topics-input">
                Topics <span className="text-danger">*</span>
                <OverlayTrigger
                  key="right"
                  placement="right"
                  overlay={
                    <Tooltip id="tooltip-right">
                      These are the general topic area(s) that the journalists&apos; stories would be about.
                    </Tooltip>
                  }
                >
                  <i className="fa-solid fa-circle-info text-muted ms-1" style={{ fontSize: "0.8em" }} title="info" />
                </OverlayTrigger>
              </label>
              <TopicsInput
                id="topics-input"
                topics={formik.values.topics}
                onChange={handleTopicsChange}
                isInvalid={topicsValidationState.isInvalid}
                isValid={topicsValidationState.isValid}
              />
              {topicsValidationState.isInvalid && (
                <div className="invalid-feedback d-block">Please add at least one topic.</div>
              )}
            </div>
            <ExpertsField
              selected={experts.selected}
              publishabilityById={experts.publishabilityById}
              atMax={experts.selected.length >= MAX_EXPERTS}
              onAdd={experts.add}
              onRemove={experts.remove}
              onSuggest={experts.suggest}
              onClear={experts.clear}
              error={
                formik.touched.selected_expert_ids && formik.errors.selected_expert_ids
                  ? formik.errors.selected_expert_ids
                  : null
              }
            />
          </div>

          {/* Email Preview Section (right) */}
          <div style={{ flex: "1 1 60%", padding: "2rem 3rem", minWidth: "0" }}>
            <EmailPreview
              storyIdea={formik.values}
              marketingPersona={mainSelectedMarketingPersona}
              selectedExperts={experts.selected}
            />
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title={
          <>
            Recipients <span className="fw-normal text-muted">({recipientCount})</span>
          </>
        }
        defaultOpen
      >
        <div style={{ padding: "1.5rem 3rem 2rem" }}>
          <RecipientsField
            pool={recipientDisplayPool}
            initialIds={formik.values.selected_recipient_ids}
            initialGroups={formik.values.selected_recipient_groups}
            onPickerSave={handlePickerSave}
            verdicts={formik.values.ai_verdicts}
            storyIdea={verdictStoryIdea}
            onVerdictsChange={handleVerdictsChange}
            // Gate recipient picking on the story being described: you
            // shouldn't choose who receives a story-idea email before
            // there's a story. title + news_hook are the minimal "described"
            // bar (and what the AI vet judges against). This relocates the
            // older "vet button disabled until described" gate (#886) up to
            // picker-entry, so vetting inside the picker always has context.
            locked={!(formik.values.title?.trim() && formik.values.news_hook?.trim())}
            lockedHint="Add a title and news hook above to start picking recipients."
            error={
              formik.touched.selected_recipient_ids && formik.errors.selected_recipient_ids
                ? formik.errors.selected_recipient_ids
                : null
            }
          />
        </div>
      </CollapsibleSection>

      <SendGuardModal
        show={showSendGuard}
        onCancel={() => setShowSendGuard(false)}
        onSendAnyway={handleSendAnyway}
        unvettedCount={sendGuardCounts.unvetted}
        badCount={sendGuardCounts.bad}
        totalCount={sendGuardCounts.total}
      />
    </div>
  );
});

// Full-width collapsible section. Heading row is the click target;
// body is rendered as a child block when open. defaultOpen controls
// the initial state. headerExtra renders on the right side of the
// heading (e.g. recipient count).
function CollapsibleSection({ title, defaultOpen = true, headerExtra, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const toggle = () => setOpen((o) => !o);
  return (
    <section className="border-bottom">
      <div
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
          }
        }}
        className="d-flex align-items-center gap-3 px-4 py-3"
        style={{ cursor: "pointer", userSelect: "none", backgroundColor: "rgb(53 98 135 / 4%)" }}
      >
        <span aria-hidden="true" style={{ fontSize: "0.9rem", width: "0.9rem" }}>
          {open ? "▼" : "▶"}
        </span>
        <h2 className="mb-0 h4 fw-semibold">{title}</h2>
        {headerExtra && <div className="ms-auto small">{headerExtra}</div>}
      </div>
      {open && children}
    </section>
  );
}

export default StoryIdeaForm;
